import type {
  AppliedAction,
  CommandAction,
  CommandProposal,
  PlanDraft,
  ScreenContext,
} from "./commandTypes";

// The pure half of the command bar: what the screen is about, which actions the person ticked, the body a plan
// draft files with, and the TL;DR line the ledger keeps. Nothing here fetches or renders.

/** `/api/work.items.transition` → `work.items.transition` (the generic `api.work.call` takes the method). */
export function methodOf(path: string): string {
  return path.startsWith("/api/") ? path.slice("/api/".length) : path;
}

/** What the route says the screen is about — the keys the server resolves into facts. */
export function screenContextFor(pathname: string, search = ""): ScreenContext {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const context: ScreenContext = {
    route: pathname + (search && !search.startsWith("?") ? `?${search}` : search),
  };
  const item = /^\/work\/([A-Za-z][A-Za-z0-9]+-\d+)(?:\/|$)/.exec(pathname);
  if (item?.[1]) context.itemKey = item[1].toUpperCase();
  const project = params.get("project");
  if (project) context.projectKey = project.toUpperCase();
  const doc = /^\/documents\/([^/?#]+)/.exec(pathname);
  if (doc?.[1]) context.documentId = decodeURIComponent(doc[1]);
  if (pathname.startsWith("/documents/")) context.surface = "document";
  else if (pathname.startsWith("/collections/")) context.surface = "collection";
  else if (pathname.startsWith("/work")) context.surface = "work";
  else if (pathname.startsWith("/mail")) context.surface = "mail";
  else context.surface = "app";
  return context;
}

/** The visible words of the page — "the tldr version of whatever this is" — bounded so a small model can read them. */
export function visibleText(
  doc: Document | undefined = typeof document === "undefined" ? undefined : document,
  max = 6000,
): string {
  if (!doc) return "";
  const main = (doc.querySelector("main") ?? doc.body) as HTMLElement | null;
  const text = (main?.innerText ?? "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

/** Every action starts ticked; the person unticks what they do not want. A plan action is always one card of its own. */
export function initialSelection(proposal: CommandProposal): Set<string> {
  return new Set(proposal.actions.map((a) => a.id));
}

export function selectedActions(proposal: CommandProposal, selected: Set<string>): CommandAction[] {
  return proposal.actions.filter((a) => selected.has(a.id));
}

/** "This will: Close PLAT-7 · Close PLAT-11 · Comment on PLAT-3" — the popup's line, and the ledger's. */
export function summarizeActions(actions: CommandAction[]): string {
  return actions.map((a) => a.label).join(" · ");
}

export function anyDanger(actions: CommandAction[]): boolean {
  return actions.some((a) => a.danger);
}

/**
 * The body `work.plan.apply` takes for ONE item with ONE developer step (the brief the person edited is the step's
 * prompt and the item's description). `flows: "auto"` files a flow per item; the project's autonomy policy approves and
 * starts it, and the dev step reaches the IDE window of that project's repository through the role map.
 */
export function planApplyBody(draft: PlanDraft, sentence: string): Record<string, unknown> {
  const prompt = draft.prompt.trim();
  const acceptance = draft.acceptance.map((a) => a.trim()).filter((a) => a.length > 0);
  return {
    projectKey: draft.projectKey,
    planRef: draft.planRef,
    flows: "auto",
    source: { title: "AI command bar", text: sentence },
    items: [
      {
        ref: "item-1",
        kind: draft.kind,
        title: draft.title.trim(),
        description: prompt,
        acceptance,
        steps: [
          {
            ref: "implement",
            title: "Implement",
            role: "dev",
            prompt,
            results: ["done"],
            next: { done: "accept" },
          },
        ],
      },
    ],
  };
}

export function planReady(draft: PlanDraft | null | undefined): boolean {
  return (
    !!draft && !!draft.projectKey && draft.title.trim().length > 0 && draft.prompt.trim().length > 0
  );
}

/** What `work.plan.apply` answered, reduced to what the person needs: the key and where it lives. */
export function filedItem(result: unknown): { key: string; url: string } | null {
  const r = result as { items?: Array<{ key?: string; url?: string }>; pageUrl?: string } | null;
  const first = r?.items?.[0];
  if (!first?.key) return null;
  return { key: first.key, url: first.url ?? `/work/${first.key}` };
}

export function appliedLine(applied: AppliedAction[]): string {
  const ok = applied.filter((a) => a.ok).length;
  const failed = applied.length - ok;
  return failed === 0 ? `${ok} done` : `${ok} done · ${failed} failed`;
}
